import { TFunction } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import { ZodLiteral, ZodNullable, ZodNumber, ZodOptional, ZodString, ZodUnion, z } from 'zod';

interface StringValidationSchemaArgs {
  t: TFunction;
  min?: number;
  max?: number;
}

export const getStringValidationSchema = ({ t, min, max }: StringValidationSchemaArgs): ZodString =>
  z
    .string()
    .trim()
    .min(1, t('errors.required_field'))
    .min(min || 2, t('errors.field_too_short'))
    .max(max || 4000, t('errors.field_too_long'));

export const getOptionalStringValidationSchema = (): ZodOptional<ZodNullable<ZodString>> =>
  z.string().trim().nullable().optional();

export const fileValidationSchema = () => z.instanceof(File).array();

interface NumberValidationSchemaArgs {
  t: TFunction;
  min?: number;
  max?: number;
}

export const getNumberValidationSchema = ({ t, min, max }: NumberValidationSchemaArgs): ZodNumber =>
  z
    .number()
    .min(min || 1, t('errors.required_field'))
    .max(max || 1000000000, t('errors.field_too_long'));

export const getNumericStringValidationSchema = ({ t, minLength, maxLength }: any) =>
  z
    .string()
    .min(minLength || 1, t('errors.required_field'))
    .max(maxLength || 10, t('errors.field_too_long'))
    .regex(/^[0-9]+$/, t('errors.numbers_only_allowed'));

export const getOptionalNumberValidationSchema = (): ZodOptional<
  ZodNullable<ZodUnion<[ZodNumber, ZodLiteral<''>]>>
> => z.number().or(z.literal('')).nullable().optional();

export const isInvalidField = (value: number | string | string[] | null | undefined) => {
  if (typeof value === 'number') {
    return value < 0;
  }

  return isEmpty(value);
};

export const getPhoneValidationSchema = (t: TFunction) =>
  z
    .union([z.null(), z.literal(''), z.string().length(12, t('errors.invalid_phone_length'))])
    .optional();

export const getEmailValidationSchema = (t: TFunction) =>
  z.string().min(1, t('errors.required_field')).email(t('errors.invalid_email'));

export const getOptionalEmailValidationSchema = (t: TFunction) =>
  z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().email(t('errors.invalid_email')).optional());
